import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Chip, makeStyles } from '@material-ui/core';
import { CapaQuotationServiceType } from '@generated/graphql';
import { Enum_Action_Button_Enum } from '@hooks/useUserActionLog';
import { CustomizedTypography } from '@components/Customized';
import { colorSet } from '@components/Provider';
import useRequest from '@hooks/useRequest';
import ProfileUpdateDialog from '@components/Dialog/Account/ProfileUpdateDialog';
import useQueryString from '@hooks/useQueryString';
import {
  DesignRequestStageEnum,
  PcbRequestStageEnum,
  RequestQueryString
} from '../../types';

interface ServiceTypeCardProps {
  id: CapaQuotationServiceType | string;
  img: string;
  imgMobile: string;
  serviceName: string;
  knowhow: string;
  knowhowLog?: keyof typeof Enum_Action_Button_Enum;
  isToBeReleased?: boolean;
}

const ServiceTypeCard: React.FC<ServiceTypeCardProps> = ({
  id,
  img,
  serviceName,
  isToBeReleased = false
}) => {
  const history = useHistory();

  const [openRequiredInfoForm, setOpenRequiredInfoForm] = useState(false);
  const { getQueryString } = useQueryString<RequestQueryString>();
  const { onUpdateRfqInfo, initialState } = useRequest();
  const { cardWrapper, card, toBeReleaseadLabel } = useServiceTypeCardStyles({
    src: id !== 'SURVEY' ? img : undefined,
    isToBeReleased
  });

  const onOpenRequiredInfoForm = (value: boolean) =>
    setOpenRequiredInfoForm(value);

  const onClick = useCallback(
    (e) => {
      onUpdateRfqInfo({
        ...initialState,
        serviceType: id as CapaQuotationServiceType
      });
      history.push({
        pathname: `/request`,
        search: getQueryString({
          serviceType: id as CapaQuotationServiceType,
          stage:
            id === CapaQuotationServiceType.Pcb
              ? PcbRequestStageEnum.PROCESS_SELECT
              : id === CapaQuotationServiceType.Design
              ? DesignRequestStageEnum.PROCESS_SELECT
              : undefined
        })
      });
    },
    [id, history, initialState, onUpdateRfqInfo, getQueryString]
  );

  return (
    <Box className={`${card} `} onClick={onClick}>
      {openRequiredInfoForm && (
        <ProfileUpdateDialog
          open={true}
          onClose={() => onOpenRequiredInfoForm(false)}
        />
      )}
      {isToBeReleased && (
        <Chip
          className={toBeReleaseadLabel}
          label={
            <CustomizedTypography
              variant="caption"
              color="primary"
              fontWeight="500"
            >
              출시예정
            </CustomizedTypography>
          }
        />
      )}
      <div className={`${cardWrapper} shadow`}>
        <CustomizedTypography variant="h4" fontWeight="500">
          <i className="request-gtm-serviceType-btn">{serviceName}</i>
        </CustomizedTypography>
      </div>
    </Box>
  );
};

export default ServiceTypeCard;

const useServiceTypeCardStyles = makeStyles((theme) => ({
  card: {
    backgroundImage: ({ src }: { src?: string }) => `url(${src})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    width: 200,
    height: 225,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: ({
      src,
      isToBeReleased
    }: {
      src?: string;
      isToBeReleased: boolean;
    }) => (isToBeReleased ? 'space-between' : 'flex-end'),

    '& h4': {
      color: colorSet.gray000
    },

    '&:hover': {
      cursor: 'pointer',
      '& h4': {
        color: colorSet.primary500
      },
      '& .shadow': {
        height: '100%'
      }
    }
  },

  cardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%)',
    width: 200,
    height: '50%',
    borderRadius: 16,
    padding: 20
  },

  toBeReleaseadLabel: {
    backgroundColor: colorSet.primary200,
    borderRadius: '8px',
    height: 26,
    width: 60,
    transform: 'translate(20px, 20px)',
    '& .MuiChip-label': {
      padding: '4px 8px'
    }
  }
}));
